import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CardContent,
    Checkbox,
    Chip,
    FormControlLabel,
    Grid,
    List,
    ListItem,
    ListItemButton,
    Paper,
    TextField
} from '@mui/material';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { IconBuildingSkyscraper } from '@tabler/icons';
import debounce from 'lodash.debounce';
import api from '../../../../services/api';
import PageHeader from '../../../../../ui-component/extended/PageHeader';
import GlassCard from '../../../../../ui-component/extended/GlassCard';

const EstablishmentForm = () => {
    const history = useHistory();
    const location = useLocation();
    const { id } = useParams();
    const isEdit = Boolean(id);

    // Quando aberto a partir da tela da empresa (fluxo normal agora), a
    // empresa já vem definida via `history.push(..., { organization })` -
    // não faz sentido pedir pra buscar de novo a mesma empresa que o
    // usuário acabou de clicar. Some sempre que houver contexto (seja da
    // navegação, seja da unidade já existente carregada no modo edição);
    // só cai de volta pro buscador se alguém abrir a URL direto, sem
    // contexto nenhum.
    const contextOrganization = location.state?.organization || null;

    const [appId, setAppId] = useState(null);
    const [status, setStatus] = useState({ type: '', message: '' });
    const [loading, setLoading] = useState(isEdit);
    const [code, setCode] = useState(null);

    const [orgSearch, setOrgSearch] = useState('');
    const [orgResults, setOrgResults] = useState([]);
    const [showOrgResults, setShowOrgResults] = useState(false);
    const [organization, setOrganization] = useState(contextOrganization);

    const [name, setName] = useState('');
    const [cnpj, setCnpj] = useState('');
    const [isMatriz, setIsMatriz] = useState(false);
    const [address, setAddress] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get('v1/apps/default/');
                setAppId(res.data.app.id);
            } catch (error) {
                console.error(error);
                setStatus({ type: 'error', message: 'Erro ao buscar app padrão' });
            }
        })();
    }, []);

    useEffect(() => {
        if (!appId || !isEdit) return;
        (async () => {
            try {
                const res = await api.get(`v1/apps/${appId}/establishments/${id}/`);
                const data = res.data;
                setCode(data.code);
                setName(data.name);
                setCnpj(data.cnpj || '');
                setIsMatriz(data.is_matriz);
                setAddress(data.address || '');
                setPhone(data.phone || '');
                setEmail(data.email || '');
                setOrganization({ id: data.organization, name: data.organization_name });
                setOrgSearch(data.organization_name);
            } catch (error) {
                console.error(error);
                setStatus({ type: 'error', message: 'Erro ao carregar unidade' });
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId, isEdit, id]);

    const handleOrgSearch = useMemo(
        () =>
            debounce(async (value, currentAppId) => {
                if (!value || !currentAppId) {
                    setOrgResults([]);
                    setShowOrgResults(false);
                    return;
                }
                try {
                    const res = await api.get(`v1/apps/${currentAppId}/organizations/?search=${encodeURIComponent(value)}`);
                    setOrgResults(res.data.organizations || []);
                    setShowOrgResults(true);
                } catch (error) {
                    console.error(error);
                }
            }, 400),
        []
    );

    const handleOrgSearchChange = (e) => {
        const value = e.target.value;
        setOrgSearch(value);
        setOrganization(null);
        handleOrgSearch(value, appId);
    };

    const selectOrganization = (org) => {
        setOrganization(org);
        setOrgSearch(org.name);
        setShowOrgResults(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus({ type: '', message: '' });

        if (!appId) {
            setStatus({ type: 'error', message: 'App não carregado ainda' });
            return;
        }
        if (!organization) {
            setStatus({ type: 'error', message: 'Selecione a empresa' });
            return;
        }
        if (!name) {
            setStatus({ type: 'error', message: 'Informe o nome da unidade' });
            return;
        }

        const payload = {
            organization: organization.id,
            name,
            cnpj: cnpj || null,
            is_matriz: isMatriz,
            address: address || null,
            phone: phone || null,
            email: email || null
        };

        try {
            if (isEdit) {
                await api.patch(`v1/apps/${appId}/establishments/${id}/`, payload);
            } else {
                await api.post(`v1/apps/${appId}/establishments/`, payload);
            }
            history.push(`/dashboard/config/organizations/edit/${organization.id}`);
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao salvar unidade' });
        }
    };

    if (loading) return null;

    return (
        <Box>
            <PageHeader
                icon={IconBuildingSkyscraper}
                gradient="teal"
                title={isEdit ? 'Editar Unidade' : 'Nova Unidade'}
                subtitle={organization ? `Empresa: ${organization.name}` : undefined}
                actions={code ? <Chip label={`#${code}`} /> : undefined}
            />

            <GlassCard>
                <CardContent>
                    {status.message && (
                        <Alert severity={status.type} sx={{ mb: 2 }}>
                            {status.message}
                        </Alert>
                    )}

                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} sx={{ position: 'relative' }}>
                                {organization ? (
                                    <TextField label="Empresa" fullWidth value={organization.name} disabled />
                                ) : (
                                    <>
                                        <TextField
                                            label="Empresa"
                                            fullWidth
                                            required
                                            value={orgSearch}
                                            onChange={handleOrgSearchChange}
                                            onFocus={() => orgResults.length > 0 && setShowOrgResults(true)}
                                            placeholder="Buscar por nome ou CNPJ..."
                                        />
                                        {showOrgResults && orgResults.length > 0 && (
                                            <Paper sx={{ position: 'absolute', zIndex: 10, width: '100%', maxHeight: 240, overflowY: 'auto' }}>
                                                <List dense>
                                                    {orgResults.map((org) => (
                                                        <ListItem key={org.id} disablePadding>
                                                            <ListItemButton onClick={() => selectOrganization(org)}>
                                                                {org.name} {org.cnpj ? `- ${org.cnpj}` : ''}
                                                            </ListItemButton>
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </Paper>
                                        )}
                                    </>
                                )}
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField label="Nome da unidade" fullWidth required value={name} onChange={(e) => setName(e.target.value)} />
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <TextField label="CNPJ" fullWidth value={cnpj} onChange={(e) => setCnpj(e.target.value)} />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField label="Telefone" fullWidth value={phone} onChange={(e) => setPhone(e.target.value)} />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField label="Email" type="email" fullWidth value={email} onChange={(e) => setEmail(e.target.value)} />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField label="Endereço" fullWidth value={address} onChange={(e) => setAddress(e.target.value)} />
                            </Grid>

                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={<Checkbox checked={isMatriz} onChange={(e) => setIsMatriz(e.target.checked)} />}
                                    label="É a matriz"
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Box display="flex" justifyContent="flex-end" gap={2}>
                                    <Button
                                        onClick={() =>
                                            organization
                                                ? history.push(`/dashboard/config/organizations/edit/${organization.id}`)
                                                : history.push('/dashboard/config/organizations/view')
                                        }
                                    >
                                        Voltar
                                    </Button>
                                    <Button type="submit" variant="contained">
                                        {isEdit ? 'Salvar Alterações' : 'Criar Unidade'}
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </form>
                </CardContent>
            </GlassCard>
        </Box>
    );
};

export default EstablishmentForm;
